/**
 * What a Google calendar's health means to the person, in one place, so the
 * Settings page, the sheet and the gear's count cannot disagree. Kept out of
 * `index.ts` so components in this folder can import it without a cycle.
 */

/** Anything carrying a provider's sync health, as the API sends it. */
type SyncHealth = { sync_status?: string | null };

/** A Google calendar row's health, as `CalendarSerializer` sends it. */
export type GoogleHealth = SyncHealth & {
  is_enabled?: boolean | null;
  pause_reason?: string | null;
  last_error?: string | null;
};

/**
 * A Google calendar the person switched off. It was not syncing by choice, so
 * it reads "Off" and never needs the person, whatever its stored status: rows
 * paused before the server stopped touching switched-off calendars (#356) can
 * still say paused / auth.
 */
export function isSwitchedOffGoogle(cal: GoogleHealth): boolean {
  return cal.is_enabled === false;
}

/** An enabled Google calendar paused because the account's access was lost. */
export function isGoogleAuthPause(cal: GoogleHealth): boolean {
  return cal.is_enabled !== false && cal.sync_status === 'paused' && cal.pause_reason === 'auth';
}

/**
 * A Google calendar the server gave up on because Google says it no longer
 * exists or cannot be read (deleted, or an import Google refuses). The server
 * writes `disabled` + `not_found` and leaves it switched on
 * (`calendar_sync/services.py`, the not_found branch of `sync_events`), so it
 * is a problem of its own even though its status is not `paused`.
 */
export function isGoogleCalendarGone(cal: GoogleHealth): boolean {
  return cal.sync_status === 'disabled' && cal.pause_reason === 'not_found';
}

/**
 * The reason to show under one Google row: this calendar's own problem
 * (`permission`, or gone), in the server's words. Null for a healthy row or a
 * switched-off row. A row paused for `auth` is neither of the two, so it gets
 * nothing here: the account-level notice speaks for it.
 */
export function googleRowError(cal: GoogleHealth): string | null {
  if (isSwitchedOffGoogle(cal)) return null;
  const ownProblem = (cal.sync_status === 'paused' && cal.pause_reason === 'permission')
    || isGoogleCalendarGone(cal);
  return ownProblem && cal.last_error ? cal.last_error : null;
}

/**
 * A lost Google grant is ONE account problem: the server pauses every enabled
 * Google calendar with the same message. Settings says it once, above the
 * rows. Null when no enabled calendar lost access; otherwise the message the
 * server wrote ('' if it wrote none).
 */
export function googleAuthNotice(google: GoogleHealth[] | null | undefined): string | null {
  const rows = (google ?? []).filter(isGoogleAuthPause);
  if (rows.length === 0) return null;
  return rows.find((c) => !!c.last_error)?.last_error ?? '';
}
